import { forwardRef } from 'react';
import { Button, type ButtonProps } from '@chakra-ui/react';

export type RiftboundButtonTreatment = 'primary' | 'secondary' | 'ghost';

export type RiftboundButtonSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg';

export type RiftboundButtonProps = Omit<ButtonProps, 'size' | 'variant'> & {
  treatment?: RiftboundButtonTreatment;
  size?: RiftboundButtonSize;
  /** Recipe variant. `primary`, `outline`, `ghost`, `menuItem`, and `selected` come from the button recipe. */
  variant?: ButtonProps['variant'] | 'primary' | 'menuItem' | 'selected';
};

export { pressActive as RIFTBOUND_PRESS_ACTIVE } from '../../theme/recipes/press';

const treatmentVariant = {
  primary: 'primary',
  secondary: 'outline',
  ghost: 'ghost',
} as const;

/**
 * Shared button. Look lives in the button recipe (`primary`, `outline`, `ghost`, `selected`).
 * `treatment` is the public name for the three everyday variants.
 */
export const RiftboundButton = forwardRef<HTMLButtonElement, RiftboundButtonProps>(
  function RiftboundButton(
    { treatment = 'secondary', size = 'md', variant, colorPalette, ...rest },
    ref,
  ) {
    const mapped = variant ?? treatmentVariant[treatment];
    const palette = colorPalette ?? (treatment === 'primary' && mapped === 'primary' ? 'brandGold' : undefined);
    return (
      <Button
        ref={ref}
        {...rest}
        size={size}
        variant={mapped as ButtonProps['variant']}
        colorPalette={palette}
      />
    );
  },
);
